import { isNonEmptyArray } from '@tanstack/react-form'
import { parseSqlAst } from '@vendia/management-api-common/src/sql/flora-parser'
import { AddLakehouseConnectionDocument, AddLakehouseTableDocument } from '@vendia/control-plane-api'
import {
  DataPolicyColumnEffect,
  LakehouseConnectionInput,
  LakehouseConnectionTypeEnum,
  LakehouseSyncScheduleEnum,
  LakehouseTableInput,
  LakehouseTableStatusEnum,
  Uni,
} from '@vendia/management-api-types'
import debug from 'debug'
import { useNavigate, useParams } from 'react-router'
import MultiStepFlow from 'src/components/flows/multi-step-flow'
import Loader from 'src/components/loaders/page-loader'
import { ALL_PARTNERS_STAR } from 'src/components/tables/sharing-rules-table.utils'
import { StepId } from 'src/pages/uni-create/config'
import { StepChooseSource } from 'src/pages/uni-create/flow-lakehouse/choose-source.step'
import { StepConfigureCsv } from 'src/pages/uni-create/flow-lakehouse/configure-csv.step'
import { StepConnectBigQuery } from 'src/pages/uni-create/flow-lakehouse/connect-bigquery.step'
import { StepConnectCloudera } from 'src/pages/uni-create/flow-lakehouse/connect-cloudera.step'
import { StepConnectDatabricks } from 'src/pages/uni-create/flow-lakehouse/connect-databricks.step'
import { StepConnectMySql } from 'src/pages/uni-create/flow-lakehouse/connect-mysql.step'
import { StepConnectPostgres } from 'src/pages/uni-create/flow-lakehouse/connect-postgres.step'
import { StepConnectS3 } from 'src/pages/uni-create/flow-lakehouse/connect-s3.step'
import { StepConnectSnowflake } from 'src/pages/uni-create/flow-lakehouse/connect-snowflake.step'
import { StepCreateColumnSharingPermissions } from 'src/pages/uni-create/flow-lakehouse/create-column-sharing-permissions.step'
import { StepCreateRowSharingPermissions } from 'src/pages/uni-create/flow-lakehouse/create-row-sharing-permissions.step'
import { StepJoinVendiaTables } from 'src/pages/uni-create/flow-lakehouse/join-vendia-tables.step'
import { StepReviewMultipleTables } from 'src/pages/uni-create/flow-lakehouse/review-multi-table.step'
import { StepReviewSharingPermissions } from 'src/pages/uni-create/flow-lakehouse/review-sharing-permissions.step'
import { StepSelectConnection } from 'src/pages/uni-create/flow-lakehouse/select-connection.step'
import { StepSelectS3Files } from 'src/pages/uni-create/flow-lakehouse/select-s3-files.step'
import { StepSelectTable } from 'src/pages/uni-create/flow-lakehouse/select-table.step'
import { StepSelectUpdateColumns } from 'src/pages/uni-create/flow-lakehouse/select-update-column.step'
import { StepSelectVendiaTables } from 'src/pages/uni-create/flow-lakehouse/select-vendia-tables.step'
import { StepTableSettings } from 'src/pages/uni-create/flow-lakehouse/table-settings.step'
import { StepUploadFile } from 'src/pages/uni-create/flow-lakehouse/upload-file.step'
import analytics, { EVENTS } from 'src/utils/analytics'
import { generateTableAclInputFromAccessLevel } from 'src/utils/csv-uploader/graphql.utils'
import { useAddTableTaskTriggerConfig } from 'src/utils/hooks/use-add-table-task-trigger-config'
import { useAddTaskDefinition } from 'src/utils/hooks/use-add-task-definition'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { useGetLakehouseConnections } from 'src/utils/hooks/use-get-lakehouse-connections'
import { useGetLakehouseData } from 'src/utils/hooks/use-get-lakehouse-data'
import { useGetUni } from 'src/utils/hooks/use-get-uni'
import { useRequestTaskRun } from 'src/utils/hooks/use-request-task-run'
import { transformJoinTableColumns } from 'src/utils/lakehouse/column-mapping'
import { DatasetType, LakehouseFlowStepValues, TaskDefinitionType } from 'src/utils/lakehouse/types'
import notify from 'src/utils/notify'
import { generateShortId } from 'src/utils/short-id'

const logger = debug('app:createVendiaTableFlow')

export const CreateVendiaTableFlow = () => {
  const { data: uniData } = useGetUni()
  const uni = uniData?.getUni as Uni
  const { node: nodeName } = useParams()
  const { data, refetch } = useGetLakehouseData(false) // Get all tables including deleted ones
  const useGetLakehouseConnectionsResponse = useGetLakehouseConnections()
  const requestTaskRunMutation = useRequestTaskRun()
  const addTaskDefinitionMutation = useAddTaskDefinition()
  const addTableTaskTriggerConfigMutation = useAddTableTaskTriggerConfig()

  const navigate = useNavigate()
  const client = useControlPlaneApi()

  const allTables = data?.tables ?? []
  const existingTables = allTables.filter((table) => table.status !== LakehouseTableStatusEnum.Deleted)
  const deletedTables = allTables.filter((table) => table.status === LakehouseTableStatusEnum.Deleted)

  const addConnection = async (
    connectionName: string,
    connectionId: string,
    source: LakehouseFlowStepValues['source'],
  ) => {
    const connectionType = source.connection!
    let connectionConfig = {}

    switch (connectionType) {
      case LakehouseConnectionTypeEnum.Snowflake:
        connectionConfig = { snowflakeConnection: source.snowflake }
        break
      case LakehouseConnectionTypeEnum.Cloudera:
        connectionConfig = { clouderaConnection: source.cloudera }
        break
      case LakehouseConnectionTypeEnum.Postgres:
        connectionConfig = { postgresConnection: source.postgres }
        break
      case LakehouseConnectionTypeEnum.Mysql:
        connectionConfig = { mysqlConnection: source.mysql }
        break
      case LakehouseConnectionTypeEnum.Databricks:
        connectionConfig = { databricksConnection: source.databricks }
        break
      case LakehouseConnectionTypeEnum.Bigquery:
        connectionConfig = { bigqueryConnection: source.bigquery }
        break
      case LakehouseConnectionTypeEnum.S3:
        connectionConfig = {
          s3Connection: {
            crossAccountRoleArn: source.s3?.crossAccountRoleArn,
            bucket: source.s3?.bucketName,
            bucketRegion: source.s3?.bucketRegion,
          },
        }
        break
    }

    const connection: LakehouseConnectionInput = {
      id: connectionId,
      name: connectionName,
      type: connectionType,
      ...connectionConfig,
    }

    return await client.graphqlFetch({ query: AddLakehouseConnectionDocument, variables: {
      input: {
        uniName: uni.name,
        nodeName: nodeName!,
        connection,
      },
    } })
  }

  const addTable = async ({ value }: { value: LakehouseFlowStepValues }) => {
    const { product: table, source, accessLevel } = value
    const existingIds = existingTables.map((product) => product.key)
    const shortId = generateShortId({ existingIds })
    existingIds.push(shortId)
    const newTableInput: LakehouseTableInput = {
      key: shortId,
      name: table.name,
      syncSchedule: table.syncSchedule,
      tableDefinition: {
        columnPolicies: table.columnPolicies,
        rowPolicies: table.rowPolicies,
        defaultColumnPolicyEffect: table.defaultColumnPolicyEffect,
      },
      sourceConnectionId: value.connectionId ?? '',
      sourceConnectionType: source.connection!,
      sourceTableDefinitions: [
        {
          tableName: source.selectedTable,
          columns: value.csvConfig
            ? Object.entries(value.csvConfig.columns).map(([columnName, columnType]) => ({
                name: columnName as string,
                type: (columnType === 'TIME' ? 'VARCHAR' : columnType) as string, // no support in Glue for Iceberg TIME type
                primaryKey: false,
                nullable: true,
                uniqueKey: false,
              }))
            : table.joinSql
              ? transformJoinTableColumns(value.selectedTableColumns)
              : value.selectedTableColumns,
          lastUpdatedColumn: value.lastUpdatedColumn,
          lastUpdatedColumnFormat: value.lastUpdatedColumnFormat,
          keyColumns: value.keyColumns,
        },
      ],
      joinQuery: table.joinSql ? parseSqlAst(table.joinSql) : undefined,
      csvConfig: value.csvConfig
        ? {
            ...value.csvConfig,
            fileName: value.csvConfig.fileName ?? source.selectedTable ?? '',
          }
        : undefined,
    }

    const aclInput = generateTableAclInputFromAccessLevel(accessLevel)

    const addLakehouseTableResponse = await client.graphqlFetch({ query: AddLakehouseTableDocument, variables: {
      input: {
        uniName: uni.name,
        nodeName: nodeName!,
        table: newTableInput,
        aclInput,
      },
    } })

    const addLakehouseTableTransactionId = addLakehouseTableResponse.addLakehouseTable

    let tableId: string | undefined = undefined

    if (addLakehouseTableResponse.errors && !addLakehouseTableTransactionId) {
      const errorMessage = addLakehouseTableResponse.errors.map((error) => error.message).join(', ')
      analytics.track(EVENTS.TABLE_CREATE_FAILED, {
        uniName: uni.name,
        nodeName: nodeName!,
        tableName: table.name,
        connectionType: source.connection,
        errorMessage,
      })
      notify.error(`Error adding table: ${errorMessage}`)
      return false
    }

    // addLakehouseTable is async, so we need to poll for the table creation
    const pollStartTime = Date.now()
    const pollTimeout = 45000 // Poll for up to 45 seconds
    const pollInterval = 2000 // Wait 2 seconds between polls

    while (Date.now() - pollStartTime < pollTimeout) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval))
      const refetchResult = await refetch()

      const fetchedTables = refetchResult.data?.tables ?? []
      const foundTable = fetchedTables.find((t) => t.name === table.name)

      if (foundTable) {
        tableId = foundTable._id
        break
      }
    }

    if (!tableId) {
      analytics.track(EVENTS.TABLE_CREATE_FAILED, {
        uniName: uni.name,
        nodeName: nodeName!,
        tableName: table.name,
        connectionType: source.connection,
        errorMessage: 'Table not found after polling timeout',
      })
      notify.error('Table creation failed')
      return false
    }

    // Could be more elegant here, and we may want to also pass in table._id as source.identifier for single Vendia tables
    const buildSources = () => {
      if (table.joinSql) {
        const selectedVendiaTables = value.product.selectedVendiaTables || []
        return selectedVendiaTables.map((vendiaTable: any) => ({
          type: DatasetType.TABLE,
          identifier: vendiaTable._id,
          connectionId: '',
        }))
      }

      return [
        {
          type: DatasetType.TABLE,
          identifier: source.selectedTable,
          connectionId: value.connectionId ?? '',
        },
      ]
    }

    const taskId = await addTaskDefinitionMutation.mutateAsync({
      name: `add_${table.name}`,
      sources: buildSources(),
      destination: {
        type: DatasetType.TABLE,
        identifier: tableId,
        connectionId: value.connectionId ?? '',
      },
      type: TaskDefinitionType.INGESTION,
      typeId: tableId,
    })

    await addTableTaskTriggerConfigMutation.mutateAsync({
      tableId,
      chainConfig: [
        {
          taskId: taskId._id,
          sourceChainMode: 'ALL',
        },
      ],
      schedule: [
        {
          cron: table.syncSchedule,
          taskId: taskId._id,
        },
      ],
    })

    await requestTaskRunMutation.mutate({ tableId, taskId: taskId._id })

    analytics.track(EVENTS.TABLE_CREATED, {
      uniName: uni.name,
      nodeName: nodeName!,
      tableName: table.name,
      tableId,
      connectionType: source.connection,
      syncSchedule: table.syncSchedule,
      hasJoinSql: !!table.joinSql,
      columnCount: value.selectedTableColumns?.length || 0,
    })

    notify.success('Table added successfully!')
    return true
  }
  if (!uni) {
    logger('uni or shareAppConfig not found', uni)
    return (
      <div className='grid place-items-center'>
        <Loader />
      </div>
    )
  }

  return (
    <MultiStepFlow<LakehouseFlowStepValues>
      initialFlowState={{
        flowType: 'addTable',
        accessLevel: {
          // Adding a key for each partner node name in uni.nodes defaulting to 'READ' for now
          [ALL_PARTNERS_STAR]: 'READ',
          ...uni?.nodes
            ?.filter((node) => node.name !== nodeName)
            .reduce((acc, node) => ({ ...acc, [node.name]: 'READ' }), {}),
        },
        uniName: uni.name,
        nodeName: nodeName!,
        // TODO this field is still named 'product' for now, but it really should be "table" or "target" for something more generic
        product: {
          name: '',
          columnPolicies: [],
          rowPolicies: [],
          defaultColumnPolicyEffect: DataPolicyColumnEffect.Exclude,
          syncSchedule: LakehouseSyncScheduleEnum.OnDemand,
          selectedVendiaTables: [],
          queryDefinition: {
            leftTable: {
              table: '',
              selectedColumns: [],
            },
            rightTable: {
              table: '',
              selectedColumns: [],
            },
            joinConditions: [],
            joinType: 'INNER JOIN',
            returnAllColumns: true,
          },
        },
        selectedTables: {},
        source: {
          connection: undefined,
          availableFiles: [],
          availableTables: [],
          selectedTable: '',
          sourceRegion: '',
        },
        // Start with an empty space entry so the first dropdown is rendered
        keyColumns: [''],
        lastUpdatedColumn: '',
        lastUpdatedColumnFormat: '',
        useIncrementalUpdates: true,
        existingVendiaTables: existingTables,
        deletedVendiaTables: deletedTables,
        connectionName: '',
        connectionId: '',
      }}
      flowSteps={[
        {
          id: StepId.ChooseSource,
          StepComponent: StepChooseSource,
        },
        {
          id: StepId.ConnectBigQuery,
          StepComponent: StepConnectBigQuery,
          showInStepNav: false,
        },
        {
          id: StepId.ConnectSnowflake,
          StepComponent: StepConnectSnowflake,
          showInStepNav: false,
        },
        {
          id: StepId.ConnectCloudera,
          StepComponent: StepConnectCloudera,
          showInStepNav: false,
        },
        {
          id: StepId.ConnectPostgres,
          StepComponent: StepConnectPostgres,
          showInStepNav: false,
        },
        {
          id: StepId.ConnectMySql,
          StepComponent: StepConnectMySql,
          showInStepNav: false,
        },
        {
          id: StepId.ConnectDatabricks,
          StepComponent: StepConnectDatabricks,
          showInStepNav: false,
        },
        {
          id: StepId.ConnectS3,
          StepComponent: StepConnectS3,
          showInStepNav: false,
        },
        {
          id: StepId.SelectConnection,
          StepComponent: StepSelectConnection,
          showInStepNav: false,
        },
        {
          id: StepId.SelectS3File,
          StepComponent: StepSelectS3Files,
          showInStepNav: false,
        },
        {
          id: StepId.SelectTable,
          StepComponent: StepSelectTable,
          showInStepNav: false,
        },
        {
          id: StepId.SelectVendiaTables,
          StepComponent: StepSelectVendiaTables,
          showInStepNav: false,
        },
        {
          id: StepId.JoinVendiaTables,
          StepComponent: StepJoinVendiaTables,
          showInStepNav: false,
        },
        {
          id: StepId.TableSettings,
          StepComponent: StepTableSettings,
        },
        {
          id: StepId.SelectUpdateColumns,
          StepComponent: StepSelectUpdateColumns,
        },
        {
          id: StepId.ColumnSharingPermissions,
          StepComponent: StepCreateColumnSharingPermissions,
        },
        {
          id: StepId.RowSharingPermissions,
          StepComponent: StepCreateRowSharingPermissions,
        },
        {
          id: StepId.SharingPermissions,
          StepComponent: StepReviewSharingPermissions,
        },
        {
          id: StepId.ReviewMultipleTables,
          StepComponent: StepReviewMultipleTables,
        },
        {
          id: StepId.UploadFile,
          StepComponent: StepUploadFile,
        },
        {
          id: StepId.ConfigureCsv,
          StepComponent: StepConfigureCsv,
        },
      ]}
      onSubmit={async ({ value }: { value: any }) => {
        const { product, source, selectedTables, csvConfig } = value
        if (
          !value.connectionId &&
          !isNonEmptyArray(product.selectedVendiaTables) &&
          (!csvConfig || source.connection === LakehouseConnectionTypeEnum.S3)
        ) {
          // If there is no connectionId and no selected existing tables, the user is creating a new connection.
          const connections = useGetLakehouseConnectionsResponse.data?.connections ?? []
          value.connectionId = generateShortId({
            existingIds: connections.map((c) => c.id).filter((id): id is string => id !== undefined) ?? [],
          })
          const addConnectionResponse = await addConnection(value.connectionName.trim(), value.connectionId, source)
          if (addConnectionResponse.errors) {
            notify.error(addConnectionResponse.errors[0].message)
            return
          }
        }

        if (csvConfig) {
          product.defaultColumnPolicyEffect = DataPolicyColumnEffect.Allow
        }
        const addSuccess = await addTable({ value })

        if (addSuccess) {
          refetch()
          navigate('../', { relative: 'path' })
        }
      }}
    />
  )
}
